import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/use-api';
import type {
  BuffetAssortmentRequest,
  BuffetAssortmentResponse,
  BuffetDayResponse,
  BuffetSaleRequest,
  BuffetTransferRequest,
  BuffetWriteOffRequest,
} from './types';

/**
 * API буфета. Данные организационные — токен по умолчанию.
 *
 * Один ключ на весь раздел: продажа, приход и списание меняют дневной
 * остаток, а изменение ассортимента — список товаров на экране.
 */
const KEY = 'buffet';

/**
 * Дневной отчёт буфета. В демо-режиме запрос выключен: данные берутся из
 * локального генератора (buffet-demo-data.ts).
 */
export function useBuffetDay(objectId: string, date: string, enabled = true) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'day', objectId, date],
    queryFn: () => {
      const query = new URLSearchParams({ objectId, date });
      return api.request<BuffetDayResponse>(`/v1/buffet/day?${query.toString()}`);
    },
    enabled: enabled && api.organizationId !== null && objectId !== '',
  });
}

/** Ассортимент буфета; в демо-режиме запрос тоже выключен. */
export function useBuffetAssortment(objectId: string, enabled = true) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'assortment', objectId],
    queryFn: () =>
      api.request<BuffetAssortmentResponse>(
        `/v1/buffet/assortment?objectId=${encodeURIComponent(objectId)}`
      ),
    enabled: enabled && api.organizationId !== null && objectId !== '',
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: [KEY] });
}

export function useSaveBuffetAssortment() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: BuffetAssortmentRequest) =>
      api.request<void>('/v1/buffet/assortment', { method: 'PUT', body }),
    onSuccess: invalidate,
  });
}

export function useCreateBuffetTransfer() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: BuffetTransferRequest) =>
      api.request<{ id: string }>('/v1/buffet/transfers', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useCreateBuffetSale() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: BuffetSaleRequest) =>
      api.request<{ id: string }>('/v1/buffet/sales', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useCreateBuffetWriteOff() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: BuffetWriteOffRequest) =>
      api.request<{ id: string }>('/v1/buffet/write-offs', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}
